/**
 * ATL-1793 — the error a stock-affecting writer throws when Firestore refused its batch because
 * product documents it `update`d do not exist. Kept free of imports so a screen can test for it
 * without loading the Firestore SDK; the probe that builds it is `missingProductDocuments.ts`.
 */

export interface MissingProduct {
  id: string
  name: string
}

/**
 * The batch was refused because these products' Firestore documents do not exist. Nothing the
 * batch carried was written. `cause` is the SDK's original rejection.
 */
export class ProductDocumentsMissingError extends Error {
  readonly writer: string
  readonly products: MissingProduct[]
  readonly cause: unknown

  constructor(writer: string, products: MissingProduct[], cause: unknown) {
    super(
      `${writer}: Firestore refused the batch — product document(s) do not exist: ` +
        products.map((product) => product.id).join(', '),
    )
    this.name = 'ProductDocumentsMissingError'
    this.writer = writer
    this.products = products
    this.cause = cause
  }
}

/**
 * The screen-side test for this refusal. `instanceof` alone is not trusted across a transpiled
 * `extends Error` (older Babel class transforms break the prototype chain on some runtimes), so the
 * name and the product list are accepted as the same evidence.
 */
export const isProductDocumentsMissingError = (error: unknown): error is ProductDocumentsMissingError =>
  error instanceof ProductDocumentsMissingError ||
  ((error as { name?: unknown } | null | undefined)?.name === 'ProductDocumentsMissingError' &&
    Array.isArray((error as { products?: unknown }).products))
